import {
    Component,
    defineElement,
    FC,
    useElement,
    useElements,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { BPDRange } from '@bpd-library/components';
import { useClickOutside } from '@bpd-library/utilities';
import { transformTagLabel } from '@features/properties/components/tag-list/utilities/transform-tag-label';
import { BPDFunctionalSvg } from '@source/components/atoms';
import { ContentCollapseElement } from '@source/components/atoms/content-collapse/utilities/content-collapse';
import {
    FilterInput,
    FilterLogic,
    filterStore,
    getFilterByType,
    PropertiesFilterFunctions,
    updateFilter,
} from '@source/store/filters';
import { GTMPush } from '@source/utilities/gtm';
import { isMobile } from '@utilities/media-query';

// import style from './property-filter.scss';
import { GeneratedFiltersProps, generateInputs, transformFilterLabel } from './utilities';

export interface PropertyFilterProps {
    expanded: boolean;
    icon: string;
    title: string;
    dropdownLabel: string;
    filterName: string;
    hasRangeValues: boolean;
    filterFunctions: PropertiesFilterFunctions;
    filters: GeneratedFiltersProps;
    filterLogic?: FilterLogic;
}

export type PropertyFilterElement = Component & PropertyFilterProps;

const PropertyFilter: FC<PropertyFilterElement> = ({ element }) => {
    // Properties
    const [icon] = useProp<string>('icon', 'icons-spec-house');
    const [filterTitle] = useProp('title', '');
    const [dropdownLabel] = useProp('dropdownLabel', `Kies ${filterTitle}`);
    const [filterName] = useProp('filterName', '');
    const [onToggle] = useProp<Function>('onToggle', () => null);
    const [hasRangeValues] = useProp<boolean>('hasRangeValues', false);
    const [, setExpanded, watchExpanded] = useProp<boolean>('expanded', false);
    const [filters] = useProp<GeneratedFiltersProps>('filters', {
        type: '',
        components: [],
        rangeSliderOptions: undefined,
    });

    // Elements
    const inputElements = useElements<HTMLInputElement[]>('input');
    const title = useElement<HTMLSpanElement>('[js-hook-filter-label]');
    const contentCollapse = useElement<ContentCollapseElement>('[js-hook-filter-collapse]');
    const rangeSlider = useElement<BPDRange>('bpd-range-slider');

    // Stores and utilties
    const [, subscribe] = useStore(filterStore);

    useListen(inputElements, 'change', async (e: InputEvent) => {
        const { id, checked, name, type, value } = e.currentTarget as HTMLInputElement;
        // Update the filter within the store
        updateFilter({ id, checked, name, type, value, hasRangeValues });

        const filter = getFilterByType(filterName);
        const label =
            (await transformFilterLabel(filterName, filter)) ||
            (await transformTagLabel(name, value));

        GTMPush({
            event: 'gtm.filters',
            eventCategory: 'Woonwensen',
            eventAction: checked ? `Add ${name}` : `Remove ${name}`,
            eventLabel: label?.toString(),
        });
    });

    watchExpanded((expanded) => {
        if (isMobile()) {
            contentCollapse.current!.expanded = expanded;
        }
    });

    useClickOutside(element, (clickOutside) => {
        if (clickOutside) {
            setExpanded(false);
        }
    });

    subscribe(
        (state) => {
            const { filters, currentFilterChange } = state;

            // Only update the filter state when the current filter that is changed is equal to the name of this filter.
            if (currentFilterChange === filterName || currentFilterChange === 'all') {
                const filterItems = filters[filterName];

                // We only want to reset elements that are unchecked within the store.
                resetInputState(filterItems.inputs.filter((input) => !input.checked));

                // Update filter label based upon selected values and based upon if it has range values.
                updateFilterLabel();
            }
        },
        ['filters'],
    );

    async function updateFilterLabel() {
        if (!hasRangeValues) return;
        const label = await getLabel();

        if (label) {
            title.current!.innerHTML = '';
            title.current!.append(label);
        }
    }

    /**
     * Returns the label based upon types and selected filters of that type
     * @returns { string | null }
     */
    function getLabel() {
        if (isMobile()) return filterTitle;
        const filter = getFilterByType(filterName);
        return transformFilterLabel(filterName, filter) || filterTitle;
    }

    /**
     * Reset inputs based upon their state within the filter store.
     * @param {FilterInput[]} inputs
     */
    function resetInputState(inputs: FilterInput[]) {
        if (inputElements.current) {
            inputs.forEach((input) => {
                // Get related input based upon if one of the current input elements of this filter has an id equal to an id of the store.
                const relatedInput = Array.from(inputElements.current).find(
                    (item) => item && item.id === input.id,
                );

                // If input is range slider reset the range
                if (input.type === 'number') rangeSlider.current!.resetState();

                // Check if there is a related input and if the input is not already checked
                if (relatedInput && relatedInput.checked) relatedInput.checked = false;
            });
        }
    }

    function handleFilterToggle() {
        const nextExpandedState = !element.expanded;
        onToggle();
        setExpanded(nextExpandedState);
    }

    return (
        <Fragment>
            <button class="property-filter__toggle" onClick={() => handleFilterToggle()}>
                <BPDFunctionalSvg name={icon} />
                <span class="property-filter__label" js-hook-filter-label>
                    {getLabel() || filterTitle}
                </span>
                <span class="property-filter__dropdown-chevron">
                    <BPDFunctionalSvg name={'icons-arrow-down'} />
                </span>
            </button>
            <div class="property-filter__dropdown">
                <span class="property-filter__dropdown-label">{dropdownLabel}</span>
                <bpd-content-collapse js-hook-filter-collapse class="property__filter-collapse">
                    <div class="property-filter__dropdown-filters">
                        {generateInputs({
                            type: filters.type,
                            components: filters.components,
                            rangeSliderOptions: filters.rangeSliderOptions,
                        })}
                    </div>
                </bpd-content-collapse>
            </div>
        </Fragment>
    );
};

PropertyFilter.props = {
    icon: {
        type: String,
    },
    expanded: {
        type: Boolean,
        reflectToAttr: true,
    },
    title: {
        type: String,
    },
    dropdownLabel: {
        type: String,
    },
    filterName: {
        type: String,
    },
    filters: {
        type: Object,
    },
    hasRangeValues: {
        type: Boolean,
    },
    onToggle: {
        type: Function,
    },
};

defineElement('property-filter', PropertyFilter);
